import React, { useEffect, useRef, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const WINDOW = 120;
const LABELS = Array.from({ length: WINDOW }, (_, i) => i);
const FPS = 30;
const DRAIN_HZ = 30; // matches real sample rate, so the queue neither starves nor piles up

interface IPPGChartProps {
  isMonitoring: boolean;
  ppgSignal?: number[];
  // Exact, monotonically-increasing sample count from the server
  // (result.buffer_frames) — diffed between updates to know precisely how
  // many tail samples are genuinely new. See the queue comment below.
  bufferFrames?: number;
  // Verification-demo overlay only (see RecordedDemoPage.tsx) — a real
  // lab-device reference trace, pre-aligned by the backend to the exact
  // same 30fps timeline as ppgSignal, so it can be queued/drained through
  // the identical mechanism below. Undefined on every live-monitoring page
  // (no second dataset is even created then — see hasGroundTruth).
  groundTruthSignal?: number[];
}

const IPPGChart: React.FC<IPPGChartProps> = ({ isMonitoring, ppgSignal, bufferFrames, groundTruthSignal }) => {
  const chartRef = useRef<ChartJS<'line'> | null>(null);
  // Queue of real samples waiting to be drawn, drained at a fixed real-time
  // rate — this is what makes the display continuous regardless of how often
  // (or rarely) a new server result actually arrives. Each update enqueues
  // only the genuinely-new tail, sized from the *exact* diff in
  // bufferFrames (falling back to a wall-clock estimate only if the server
  // hasn't sent one), so a sparse update (e.g. one result every ~5s at a
  // large hop) enqueues a bigger batch that still drains smoothly over that
  // same ~5s — never a jump, never a freeze, and no drift from estimating.
  const queueRef = useRef<number[]>([]);
  const gtQueueRef = useRef<number[]>([]);
  const lastUpdateAtRef = useRef<number>(0);
  const lastBufferFramesRef = useRef<number>(0);
  const hasGroundTruth = groundTruthSignal !== undefined;

  // Stable data object — never recreated, mutated in place. The second
  // dataset only exists at all when a caller actually passes
  // groundTruthSignal, so every live-monitoring usage renders byte-identical
  // to before this prop existed.
  const chartData = useMemo(() => ({
    labels: LABELS,
    datasets: [
      {
        label: 'PPG',
        data: new Array(WINDOW).fill(0) as number[],
        borderColor: '#ff6384',
        backgroundColor: 'rgba(255,99,132,0.08)',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 1.5,
      },
      ...(hasGroundTruth ? [{
        label: 'Ground truth',
        data: new Array(WINDOW).fill(0) as number[],
        borderColor: 'rgba(255,255,255,0.45)',
        backgroundColor: 'transparent',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 1,
        borderDash: [4, 3],
      }] : []),
    ],
  }), [hasGroundTruth]);

  useEffect(() => {
    if (!isMonitoring) return;
    if (!ppgSignal || ppgSignal.length === 0) return;
    const flat = ppgSignal.flat().filter((v) => typeof v === 'number' && !isNaN(v));
    if (flat.length === 0) return;
    let newCount: number;
    if (typeof bufferFrames === 'number' && bufferFrames > 0) {
      const diff = bufferFrames - lastBufferFramesRef.current;
      // A non-positive diff means a session reset (new scan) or the first
      // update — show the whole available tail rather than nothing.
      newCount = diff > 0 ? Math.min(flat.length, diff) : flat.length;
      lastBufferFramesRef.current = bufferFrames;
    } else {
      const now = performance.now();
      const dtMs = lastUpdateAtRef.current ? now - lastUpdateAtRef.current : Infinity;
      newCount = Math.min(flat.length, Math.max(1, Math.round((dtMs / 1000) * FPS)));
    }
    lastUpdateAtRef.current = performance.now();
    queueRef.current.push(...flat.slice(-newCount));
    // Safety cap — if something stalls and then dumps a huge backlog, don't
    // let the catch-up take forever; show the most recent ~20s instead.
    if (queueRef.current.length > 600) {
      queueRef.current = queueRef.current.slice(-600);
    }
    // Ground truth is pre-aligned to the exact same timeline as ppgSignal
    // (see build_demo.py), so the same newCount applies to both — no
    // separate diffing needed.
    if (groundTruthSignal && groundTruthSignal.length > 0) {
      const gtFlat = groundTruthSignal.flat().filter((v) => typeof v === 'number' && !isNaN(v));
      if (gtFlat.length > 0) {
        gtQueueRef.current.push(...gtFlat.slice(-Math.min(gtFlat.length, newCount)));
        if (gtQueueRef.current.length > 600) {
          gtQueueRef.current = gtQueueRef.current.slice(-600);
        }
      }
    }
  }, [ppgSignal, groundTruthSignal, isMonitoring, bufferFrames]);

  useEffect(() => {
    if (!isMonitoring) {
      if (chartRef.current) {
        (chartRef.current.data.datasets[0].data as number[]).fill(0);
        if (hasGroundTruth && chartRef.current.data.datasets[1]) {
          (chartRef.current.data.datasets[1].data as number[]).fill(0);
        }
        chartRef.current.update('none');
      }
      queueRef.current = [];
      gtQueueRef.current = [];
      lastUpdateAtRef.current = 0;
      lastBufferFramesRef.current = 0;
      return;
    }
    // requestAnimationFrame, not setInterval: under real main-thread load
    // (MediaPipe + WS traffic + 4 charts all competing), a delayed
    // setInterval callback can fire a backlog in a sudden burst once the
    // thread frees up — stuck, then jumps. rAF never queues up missed
    // ticks, and pacing off real elapsed time (not a fixed "1 sample per
    // tick") keeps this exactly DRAIN_HZ regardless of the display's actual
    // refresh rate (60Hz, 120Hz, or throttled under load).
    let rafId = 0;
    let lastDrainAt = performance.now();
    let carry = 0;
    const tick = (now: number) => {
      const elapsedMs = now - lastDrainAt;
      lastDrainAt = now;
      carry += (elapsedMs / 1000) * DRAIN_HZ;
      const toDrain = Math.min(Math.floor(carry), queueRef.current.length);
      carry -= Math.floor(carry);
      if (toDrain > 0 && chartRef.current) {
        const buf = chartRef.current.data.datasets[0].data as number[];
        for (let i = 0; i < toDrain; i++) {
          buf.shift();
          buf.push(queueRef.current.shift()!);
        }
        if (hasGroundTruth && chartRef.current.data.datasets[1]) {
          const gtBuf = chartRef.current.data.datasets[1].data as number[];
          const gtDrain = Math.min(toDrain, gtQueueRef.current.length);
          for (let i = 0; i < gtDrain; i++) {
            gtBuf.shift();
            gtBuf.push(gtQueueRef.current.shift()!);
          }
        }
        chartRef.current.update('none');
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isMonitoring, hasGroundTruth]);

  return (
    <Line
      ref={chartRef}
      data={chartData}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } },
      }}
    />
  );
};

export default React.memo(IPPGChart);
